import clsx from 'clsx';
import A from 'components/atoms/A/A';
import ExternalLink from 'components/atoms/ExternalLink/ExternalLink';
import Txt from 'components/atoms/Txt/Txt';
import { useRouter } from 'next/router';
import React, { ReactElement } from 'react';
import classes from './LabelledIcon.module.scss';
import { resolveActiveNav } from 'utils/navHelper';
import {
  getQmeeNavLinks,
  lifepointsNavLinks,
  mobiNavLinks,
  NavLink,
} from 'components/molecules/NavLinks/mobiNavLinks';
import { useIsLoggedIn } from 'components/templates/AuthenticationProvider/AuthenticationProvider';
import { useTenant } from 'hooks/useTenant';

interface LabelledIconStyleProps {
  size?: 'small' | 'normal' | 'large' | 'xlarge' | 'xxlarge';
  color?: 'primary' | 'secondary' | 'default';
  label?: string;
  href?: string;
  link?: string;
  isSubnav?: boolean;
  borderPosition?: 'top' | 'bottom';
  isFooterNavIcon?: boolean;
}

export type Props<T extends React.ElementType = 'figure'> = {
  className?: string;
} & React.ComponentProps<T> &
  LabelledIconStyleProps;

function LabelledIcon<T extends React.ElementType = 'figure'>({
  children,
  color,
  size,
  label,
  href,
  link,
  isSubnav,
  isFooterNavIcon,
  borderPosition = 'bottom',
  ...rest
}: Props<T>): ReactElement {
  const router = useRouter();
  const isLoggedIn = useIsLoggedIn();
  const tenant = useTenant();
  const qmeeNavLinks = getQmeeNavLinks(isLoggedIn);
  const tenantNavLinks: Record<string, NavLink[]> = {
    lifepoints: lifepointsNavLinks,
    mobiworkx: mobiNavLinks,
    qmee: qmeeNavLinks,
  };
  const navLinks = tenantNavLinks[tenant] || mobiNavLinks;

  const { activeNavId, activeSubId } = resolveActiveNav(navLinks, router.asPath);

  // A top-nav item is active if it is the resolved active nav
  // A sub-nav item is active if its id matches the resolved active sub id
  const restId = rest.id === undefined ? false : rest.id === activeNavId;
  const isActiveSubnav = rest.id === activeSubId;
  const isActive = isSubnav ? isActiveSubnav : restId;

  const className = clsx(
    classes.root,
    rest.className,
    color && classes[color],
    size && classes[size],
    isActive && !isSubnav && classes.active,
    isActive && isSubnav && classes.activeSubnav,
    isActive && !isSubnav && borderPosition === 'top' && classes['border-top'],
    isActive && !isSubnav && borderPosition === 'bottom' && classes['border-bottom'],
    isSubnav && classes.subnavIcon,
    isFooterNavIcon && classes.footerNavIcon,
  );

  // If there is a link attribute, use the ExternalLink component,
  // otherwise use the A component.
  let tag = null;
  if (typeof link !== 'undefined' && link.length) {
    tag = (
      <ExternalLink link={link} href={href}>
        <Txt component="figcaption">{label}</Txt>
        {children}
      </ExternalLink>
    );
  } else if (href) {
    tag = (
      <A href={href}>
        <Txt component="figcaption">{label}</Txt>
        {children}
      </A>
    );
  } else {
    tag = (
      <div className={classes.dropdownWrapper}>
        <Txt component="figcaption">{label}</Txt>
        {children}
      </div>
    );
  }

  return (
    <figure {...rest} className={className}>
      {tag}
    </figure>
  );
}

export default LabelledIcon;
